/**
 * Bulk Sends — a durable, resumable, logged view of every credential-email
 * batch (like an import-jobs screen). Two routes:
 *   /bulk-sends            → history list of jobs
 *   /bulk-sends/:jobId     → one job: live progress + the per-recipient ledger
 *                            (intention→execution). Loads by URL so a user can
 *                            close the tab, log off, and reopen it any time.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useFormatDate } from '../hooks/useFormatDate';
import type { BulkSendJob, BulkSendItemOutcome } from '../types';

const OUTCOME_STYLES: Record<string, string> = {
  sent: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  revoked: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  deleted: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  skipped: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  pending: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  queued: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  done: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
};

// The action a job performs → its label + the success-outcome key. `send` stays
// the default so pre-action jobs render exactly as before.
const ACTION_META: Record<string, { label: string; success: BulkSendItemOutcome }> = {
  send: { label: 'Send', success: 'sent' },
  revoke: { label: 'Revoke', success: 'revoked' },
  delete: { label: 'Delete', success: 'deleted' },
};
const actionMeta = (a?: string) => ACTION_META[a || 'send'] || ACTION_META.send;

function Badge({ kind, label }: { kind: string; label: string }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${OUTCOME_STYLES[kind] || OUTCOME_STYLES.pending}`}>
      {label}
    </span>
  );
}

function ProgressBar({ job }: { job: BulkSendJob }) {
  const processed = job.sent + job.failed + job.skipped;
  const pct = job.total > 0 ? Math.min(100, Math.round((processed / job.total) * 100)) : 0;
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── History list ──────────────────────────────────────────────────────────
export function BulkSendsPage() {
  const { t } = useTranslation('dashboard');
  const formatDate = useFormatDate();
  const {
    data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['bulkSendJobs'],
    queryFn: ({ pageParam }) => api.listBulkSendJobs(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const jobs = data?.pages.flatMap((p) => p.jobs) ?? [];

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('bulkSends.title')}</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('bulkSends.subtitle')}</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('bulkSends.loading')}</p>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">
          {t('bulkSends.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700">
                <th className="px-4 py-3">{t('bulkSends.colStarted')}</th>
                <th className="px-4 py-3">{t('bulkSends.colStatus')}</th>
                <th className="px-4 py-3">{t('bulkSends.colProgress')}</th>
                <th className="px-4 py-3 text-right">{t('bulkSends.colSent')}</th>
                <th className="px-4 py-3 text-right">{t('bulkSends.colSkipped')}</th>
                <th className="px-4 py-3 text-right">{t('bulkSends.colFailed')}</th>
                <th className="px-4 py-3 text-right">{t('bulkSends.colTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.jobId} className="border-b border-gray-100 last:border-0 dark:border-gray-700/60">
                  <td className="px-4 py-3">
                    <Link to={`/bulk-sends/${j.jobId}`} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
                      {formatDate(j.createdAt)}
                    </Link>
                    <div className="mt-0.5"><Badge kind={actionMeta(j.action).success} label={t(`bulkSends.action.${j.action || 'send'}`, actionMeta(j.action).label)} /></div>
                  </td>
                  <td className="px-4 py-3"><Badge kind={j.status} label={t(`bulkSends.status.${j.status}`)} /></td>
                  <td className="px-4 py-3 w-40"><ProgressBar job={j} /></td>
                  <td className="px-4 py-3 text-right tabular-nums">{j.sent}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-amber-600 dark:text-amber-400">{j.skipped}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-red-600 dark:text-red-400">{j.failed}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">{j.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasNextPage && (
        <div className="mt-4 text-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {isFetchingNextPage ? t('bulkSends.loading') : t('bulkSends.loadMore')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

// ── Job detail (resumable) ──────────────────────────────────────────────────

export function BulkSendJobPage() {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const { t } = useTranslation('dashboard');
  const formatDate = useFormatDate();
  const [outcome, setOutcome] = useState<BulkSendItemOutcome | 'all'>('all');

  // Poll the job summary while it's still running (resumable — driven by the URL).
  const { data: job } = useQuery({
    queryKey: ['bulkSendJob', jobId],
    queryFn: () => api.getBulkSendJob(jobId),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'queued' || s === 'running' ? 2500 : false;
    },
  });

  const {
    data: itemsData, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: itemsLoading,
  } = useInfiniteQuery({
    queryKey: ['bulkSendItems', jobId, outcome],
    queryFn: ({ pageParam }) =>
      api.getBulkSendItems(jobId, { cursor: pageParam as string | undefined, outcome: outcome === 'all' ? undefined : outcome }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!jobId,
    // Refresh the ledger while the job is still processing.
    refetchInterval: job && (job.status === 'queued' || job.status === 'running') ? 4000 : false,
  });
  const items = itemsData?.pages.flatMap((p) => p.items) ?? [];

  const processed = job ? job.sent + job.failed + job.skipped : 0;
  const pending = job ? Math.max(0, job.total - processed) : 0;
  // The success outcome + label depend on what the job does (send/revoke/delete).
  const success = actionMeta(job?.action).success;
  const successLabel = t(`bulkSends.tab.${success}`, success.charAt(0).toUpperCase() + success.slice(1));
  const tabs: (BulkSendItemOutcome | 'all')[] = ['all', success, 'skipped', 'failed', 'pending'];

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
      <div className="mb-2">
        <Link to="/bulk-sends" className="text-sm text-primary-600 hover:underline dark:text-primary-400">← {t('bulkSends.backToHistory')}</Link>
      </div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('bulkSends.jobTitle')}</h1>
          {job && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{formatDate(job.createdAt)} · {scopeLabel(job, t)}</p>}
        </div>
        {job && <Badge kind={job.status} label={t(`bulkSends.status.${job.status}`)} />}
      </div>

      {/* Summary */}
      {job && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-center justify-between text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {t('bulkSends.processedOfTotal', { processed, total: job.total })}
            </span>
            {(job.status === 'queued' || job.status === 'running') && (
              <span className="text-xs text-gray-400">{t('bulkSends.keepsRunning')}</span>
            )}
          </div>
          <ProgressBar job={job} />
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={successLabel} value={job.sent} tone="text-green-600 dark:text-green-400" />
            <Stat label={t('bulkSends.colSkipped')} value={job.skipped} tone="text-amber-600 dark:text-amber-400" />
            <Stat label={t('bulkSends.colFailed')} value={job.failed} tone="text-red-600 dark:text-red-400" />
            <Stat label={t('bulkSends.pending')} value={pending} tone="text-gray-500 dark:text-gray-400" />
          </div>
          {job.scope?.capped && <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">{t('bulkSends.capped')}</p>}
        </div>
      )}

      {/* Ledger */}
      <div className="mb-3 flex flex-wrap gap-2">
        {tabs.map((o) => (
          <button
            key={o}
            onClick={() => setOutcome(o)}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              outcome === o ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {t(`bulkSends.tab.${o}`, o === 'all' ? 'All' : o.charAt(0).toUpperCase() + o.slice(1))}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500 dark:border-gray-700">
              <th className="px-4 py-3">{t('bulkSends.colRecipient')}</th>
              <th className="px-4 py-3">{t('bulkSends.colOutcome')}</th>
              <th className="px-4 py-3">{t('bulkSends.colReason')}</th>
              <th className="px-4 py-3">{t('bulkSends.colWhen')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.awardId} className="border-b border-gray-100 last:border-0 dark:border-gray-700/60">
                <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{it.recipientEmail || <span className="text-gray-400">—</span>}</td>
                <td className="px-4 py-3"><Badge kind={it.outcome} label={t(`bulkSends.tab.${it.outcome}`)} /></td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                  {it.reason ? t(`bulkSends.reason.${it.reason}`) : it.outcome === 'sent' ? t('bulkSends.reason.ok') : '—'}
                  {it.detail && <span className="ml-1 text-xs text-gray-400">({it.detail})</span>}
                </td>
                <td className="px-4 py-3 text-gray-400">{it.updatedAt ? formatDate(it.updatedAt) : '—'}</td>
              </tr>
            ))}
            {!itemsLoading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">{t('bulkSends.noRecipients')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {hasNextPage && (
        <div className="mt-4 text-center">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            {isFetchingNextPage ? t('bulkSends.loading') : t('bulkSends.loadMore')}
          </button>
        </div>
      )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900/40">
      <div className={`text-xl font-bold tabular-nums ${tone}`}>{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

function scopeLabel(job: BulkSendJob, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (job.scope?.kind === 'filter') {
    if (job.scope.filter?.notEmailedOnly) return t('bulkSends.scopeNotEmailed');
    if (job.scope.filter?.badgeId) return t('bulkSends.scopeBadge');
    return t('bulkSends.scopeFilter');
  }
  return t('bulkSends.scopeSelection', { count: job.total });
}
